import { FC, useMemo } from 'react';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import useSetSelectedTab from '@app/core/hooks/useSetSelectedTab';
import {
  setSponsorIrrAndHurdleSelectedTab,
  setSponsorIrrTableExpandYears,
} from '@app/core/store/ui-values-slice';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { shortDateViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { TABLE_COL_WIDTH, TABLE_COL_WIDTH_ALTERNATIVE } from '@app/shared/constants/table-config';
import classNames from 'classnames';
import { Navigate, useParams } from 'react-router-dom';
import styles from '../sponsor-irr-and-hurdle.module.scss';
import ForecastYearSection from './ForecastYearSection';
import { usePwcNavigate } from '@app/core/hooks/routing-hooks';
import { useExpansionState } from '@app/shared/hooks/useExpansionState';
import { DateDisambiguation } from '@app/shared/models/contracts/enums/shared-enums';
import { getDisambiguationLabel } from '@app/shared/utils/LabelHelper';
import { useFocusMode } from '@app/shared/hooks/focus-mode';
import { SponsorIrrFocusSelections } from '@app/shared/hooks/focus-mode/focus-mode-types';

const PARENT_CLASSNAME = 'sponsor-irr-table';

export interface EventColumn {
  displayDate: string;
  orderIndex: number;
  disambiguation: DateDisambiguation;
}

const SponsorIRRTable: FC = () => {
  const { l } = useLocale();
  useSetSelectedTab(RouteConstants.SponsorIRR, setSponsorIrrAndHurdleSelectedTab);
  const { generatePath } = usePwcNavigate();
  const { caseId } = useParams<{ caseId: string }>();
  const { isPwermValid, isPwermInputsValid } = useAppSelector((state) => state.project);
  const storedExpandedYears = useAppSelector((state) => state.uiValues.sponsorIrrExpandYears);

  const { isActive, isPaused, selections } = useFocusMode();
  const isBannerVisible = isActive || isPaused;
  const sponsorSelections = selections as SponsorIrrFocusSelections | undefined;

  const waterfallResults = useAppSelector(
    (state) => state.pwerm2Calculation.calculatedResults.waterfall
  );

  const caseYears = useMemo(() => {
    return caseId && waterfallResults?.cases?.[caseId]?.years;
  }, [caseId, waterfallResults]);

  const eventColumns = useMemo(() => {
    const columnMap = new Map<string, EventColumn>();

    if (caseYears) {
      Object.values(caseYears).forEach((year) => {
        Object.values(year.multiples || {}).forEach((multiple) => {
          Object.values(multiple?.institutionalIrrBreakdownData || {}).forEach((breakdown) => {
            const key = `${breakdown.displayDate}-${breakdown.orderIndex}`;
            if (!columnMap.has(key)) {
              columnMap.set(key, {
                displayDate: breakdown.displayDate,
                orderIndex: breakdown.orderIndex,
                disambiguation: breakdown.disambiguation,
              });
            }
          });
        });
      });
    }

    return Array.from(columnMap.values()).sort((a, b) => {
      const dateCompare = a.displayDate.localeCompare(b.displayDate);
      if (dateCompare !== 0) {
        return dateCompare;
      }
      return a.orderIndex - b.orderIndex;
    });
  }, [caseYears]);

  const shouldShowDisambiguationRow = useMemo(() => {
    return eventColumns.some(
      (col) =>
        col.disambiguation === DateDisambiguation.StartOfDay ||
        col.disambiguation === DateDisambiguation.EndOfDay
    );
  }, [eventColumns]);

  const allYears = useMemo(() => {
    if (caseYears) {
      return Object.entries(caseYears).map(([key, _]) => key);
    }
    return [];
  }, [caseYears]);

  const getYearId = () => {
    const selectedYear = sponsorSelections?.year;
    if (selectedYear && caseYears) {
      const entry = Object.entries(caseYears).find(
        ([_, yearData]) => yearData.exitDate === selectedYear
      );
      return entry ? entry[0] : null;
    }
    return null;
  };

  const { handleToggle, itemRefs, isExpanded } = useExpansionState({
    itemIds: allYears,
    storedExpandedIds: storedExpandedYears,
    saveExpandedIds: setSponsorIrrTableExpandYears,
    focusedItemId: isActive ? getYearId() : null,
  });

  if (!isPwermValid || !isPwermInputsValid) {
    const newPath = generatePath(`../../../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} />;
  }

  return (
    <>
      <table
        className={classNames(
          'table-primary',
          'table-primary--zebra',
          'table-primary--title-column-nowrap',
          'table-primary--sticky-first-column',
          styles[PARENT_CLASSNAME]
        )}>
        <colgroup>
          <col style={{ minWidth: `${TABLE_COL_WIDTH_ALTERNATIVE}px` }} />
          {eventColumns.map((_, index) => (
            <col key={index} style={{ width: `${TABLE_COL_WIDTH}px` }} />
          ))}
          <col style={{ width: `${TABLE_COL_WIDTH}px` }} />
        </colgroup>
        <thead
          className={`table-primary__sticky-section table-primary__sticky-section--sponsor-irr-and-hurdle-header-${
            isBannerVisible ? 'focused' : 'unfocused'
          }`}>
          <tr>
            <th className="table-primary__cell--header-tertiary table-primary__cell--vertical-separator" />
            {eventColumns.map((col, index) => (
              <th key={index} className="table-primary__cell--header-tertiary ">
                <CellValue value={col.displayDate} {...shortDateViewRules} />
              </th>
            ))}
            <th
              className={classNames(
                'table-primary__cell--header-tertiary',
                'table-primary__cell--right'
              )}>
              {l('_IRR')}
            </th>
          </tr>
          {shouldShowDisambiguationRow && (
            <tr>
              <th className="table-primary__cell--header-tertiary table-primary__cell--vertical-separator" />
              {eventColumns.map((col, index) => {
                const disambiguationLabel = getDisambiguationLabel(col.disambiguation);
                return (
                  <th
                    key={index}
                    className="table-primary__cell--header-tertiary"
                    style={{ textAlign: 'right' }}>
                    {disambiguationLabel ? `(${disambiguationLabel})` : ''}
                  </th>
                );
              })}
              <th className="table-primary__cell--header-tertiary" />
            </tr>
          )}
        </thead>
        <tbody>
          {caseYears &&
            Object.entries(caseYears).map(([forecastYearId, forecastYear]) => (
              <ForecastYearSection
                key={forecastYear.exitDate}
                forecastYearResults={caseYears[forecastYearId]}
                forecastYear={forecastYear.exitDate}
                eventColumns={eventColumns}
                isCurrentYearExpanded={isExpanded(forecastYearId)}
                onToggle={() => handleToggle(forecastYearId)}
                lastRowRef={(el: HTMLTableRowElement | null) => {
                  itemRefs.current[forecastYearId] = el;
                }}
              />
            ))}
        </tbody>
      </table>
    </>
  );
};

export default SponsorIRRTable;
